import type { BookingStatus } from '../../services/commonAreaReservationService';

/**
 * How a reservation's status is painted — one treatment, used by every surface that shows one.
 *
 * It used to be amber text on an amber background inside an amber border ("Pendente"), repeated
 * in four files with four slightly different sets of classes. On the white theme that reads as a
 * smudge: the word disappears into its own highlight. The "1 pendente" counter did the same, and
 * so did every "requer aprovação" line.
 *
 * So: a NEUTRAL chip with a coloured dot. The colour still carries the meaning at a glance, and
 * the text stays readable because it is not fighting its own background. Deliberately not applied
 * to the Nova Reserva calendar's green "free day" cells — those are a canvas the operator picks
 * from, not a label, and the green fill was asked for.
 *
 * See bookingStatusStyles.test.ts, which pins the no-amber-on-amber rule for every status.
 */

export const BOOKING_STATUSES: BookingStatus[] = [
  'Pending', 'Confirmed', 'Approved', 'Cancelled', 'Revoked',
];

const LABELS: Record<BookingStatus, string> = {
  Pending: 'Pendente',
  Confirmed: 'Confirmado',
  Approved: 'Aprovado',
  Cancelled: 'Cancelado',
  Revoked: 'Revogado',
};

const DOTS: Record<BookingStatus, string> = {
  Pending: 'bg-amber-500',
  Confirmed: 'bg-blue-500',
  Approved: 'bg-emerald-500',
  Cancelled: 'bg-gray-400',
  Revoked: 'bg-red-500',
};

export interface StatusChipStyle {
  label: string;
  /** Classes for the chip itself — neutral in both themes. */
  chip: string;
  /** Background class for the meaning-carrying dot. */
  dot: string;
}

/** The status in Portuguese; an unknown one passes through rather than rendering nothing. */
export function bookingStatusLabel(status: BookingStatus): string {
  return LABELS[status] ?? String(status);
}

export function bookingStatusChip(status: BookingStatus, lightTheme: boolean): StatusChipStyle {
  return {
    label: bookingStatusLabel(status),
    chip: lightTheme
      ? 'bg-gray-100 text-gray-700 border-gray-200'
      : 'bg-gray-800 text-gray-200 border-gray-700',
    dot: DOTS[status] ?? 'bg-gray-400',
  };
}

/**
 * The same neutral chip for counters and notices that used to be amber — "1 pendente", "requer
 * aprovação do síndico" — so one orange pairing cannot survive in a corner.
 */
export function noticeChip(lightTheme: boolean): string {
  return lightTheme
    ? 'bg-gray-100 text-gray-700 border-gray-200'
    : 'bg-gray-800 text-gray-200 border-gray-700';
}
